import React, { Key, useRef, useState, useEffect } from "react";
import "./browser_agreement_sales.css";

import { Button, Input, InputRef, Space, Table, TableColumnsType, TableProps } from "antd";
import useSWR from "swr";
import { FilterConfirmProps, FilterDropdownProps, ColumnType, SorterResult, FilterValue } from "antd/es/table/interface";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import dayjs from 'dayjs';

export interface AgreementDataType {
    key: React.Key;
    id: number;
    no_agreement: string;
    cs_code: string;
    name_customer: string;
    reference_no: string;
    disc: string;
    expired: string;
    status: string;
    document: string;
}

interface ApiResponse {
    data: AgreementDataType[];
    total: number;
}

interface TableParams {
    pagination?: {
        current?: number;
        pageSize?: number;
        total?: number;
    };
    sortField?: SorterResult<AgreementDataType>['field'];
    sortOrder?: SorterResult<AgreementDataType>['order'];
    filters?: Record<string, FilterValue | null>;
}

interface BrowserAgreementSalesProps {
    onSelectAgreement: (agreementNo: string) => void;
}

const fetcher = async ([url, params]: [string, TableParams]) => {
    const searchParams = new URLSearchParams();

    if (params.pagination?.current) {
        searchParams.append('page', params.pagination.current.toString());
    }
    if (params.pagination?.pageSize) {
        searchParams.append('pageSize', params.pagination.pageSize.toString());
    }
    if (params.sortField && params.sortOrder) {
        searchParams.append('sortBy', String(params.sortField));
        searchParams.append('order', params.sortOrder === 'ascend' ? 'asc' : 'desc');
    }

    if (params.filters) {
        Object.keys(params.filters).forEach(key => {
            const filterValue = params.filters?.[key];
            if (filterValue && filterValue.length > 0) {

                searchParams.append(key, filterValue.join(','));
            }
        });
    }

    const fullUrl = `${url}?${searchParams.toString()}`;
    const res = await fetch(fullUrl);
    if (!res.ok) {
        throw new Error(`Error fetching data: ${res.statusText}`);
    }
    return res.json();
};



const BrowserAgreementSales: React.FC<BrowserAgreementSalesProps> = ({ onSelectAgreement }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };


    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: 10,
        },
    });

    const { data: apiResponse, error, isLoading } = useSWR<ApiResponse>(
        [`${process.env.REACT_APP_BACKEND_URL}/api/agreement-data`, tableParams],
        fetcher
    );

    const data = apiResponse?.data?.map(item => ({ ...item, key: item.id }));
    const total = apiResponse?.total;

    useEffect(() => {
    }, [data, error, isLoading]);

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: keyof AgreementDataType,
    ) => {
        confirm();

        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);

        setTableParams(prev => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                current: 1,
            },
            filters: {
                ...prev.filters,
                [dataIndex]: selectedKeys.length ? selectedKeys : null,
            },
        }));
    };

    const handleReset = (clearFilters: () => void, dataIndex: keyof AgreementDataType) => {
        clearFilters();

        setSearchText('');
        setSearchedColumn('');

        setTableParams(prev => {
            const newFilters = { ...prev.filters };
            delete newFilters[dataIndex];
            return {
                ...prev,
                pagination: {
                    ...prev.pagination,
                    current: 1,
                },
                filters: newFilters,
            };
        });
    };

    const getColumnSearchProps = (dataIndex: keyof AgreementDataType): ColumnType<AgreementDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: FilterDropdownProps) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: tableParams.filters?.[dataIndex]?.length ? '#1677ff' : undefined }} />
        ),
        filterDropdownProps: {
            onOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        onFilterDropdownOpenChange: (visible: boolean) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text: string) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? String(text) : ''}
                />
            ) : (
                text
            ),
    });

    const handleTableChange: TableProps<AgreementDataType>['onChange'] = (
        pagination,
        filters,
        sorter,
    ) => {
        setTableParams({
            pagination,
            filters,
            sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
            sortField: Array.isArray(sorter) ? undefined : sorter.field,
        });
    };

    const columns: TableColumnsType<AgreementDataType> = [
        {
            title: 'No Agreement',
            dataIndex: 'no_agreement',
            key: 'no_agreement',
            ...getColumnSearchProps('no_agreement'),
            sorter: true,
        },
        {
            title: 'Customer Code',
            dataIndex: 'cs_code',
            key: 'cs_code',
            ...getColumnSearchProps('cs_code'),
            sorter: true,
        },
        {
            title: 'Customer Name',
            dataIndex: 'name_customer',
            key: 'name_customer',
            ellipsis: true,
            ...getColumnSearchProps('name_customer'),
            sorter: true,
        },
        {
            title: 'Reference No',
            dataIndex: 'reference_no',
            key: 'reference_no',
            ...getColumnSearchProps('reference_no'),
            sorter: true,
        },
        {
            title: 'Discount',
            dataIndex: 'disc',
            key: 'disc',
            ...getColumnSearchProps('disc'),
            sorter: true,
        },
        {
            title: 'Expired',
            dataIndex: 'expired',
            key: 'expired',
            ...getColumnSearchProps('expired'),
            sorter: true,
            render: (text: string) => {
                if (!text) return '';
                const date = dayjs(text);
                return date.isValid() ? date.format('DD/MM/YYYY') : text;
            },
        },
    ];

    if (error) {
        console.error('Error fetching agreements:', error);
        return <div>Gagal memuat data agreement</div>;
    }


    return (

        <div id="iqfi" style={transformStyle}>
            <div className="gjs-grid-row" id="iyok">
                <div className="gjs-grid-column" id="iwoo">
                    <Table
                        columns={columns}
                        dataSource={data}
                        size="small"
                        bordered
                        loading={isLoading}
                        pagination={{
                            ...tableParams.pagination,
                            total: total,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                        }}
                        onChange={handleTableChange}
                        onRow={(record) => { 
                            return {
                                onDoubleClick: () => {
                                    onSelectAgreement(record.no_agreement); 
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrowserAgreementSales;